import { useEffect, useMemo, useRef, useState } from "react";
import { PUNTAJE_APRUEBA } from "@huayruro/shared";
import { mutar, useApi } from "../../lib/useApi";
import { ahoraFecha } from "../../lib/fecha-ui";
import type { SesionActiva } from "../../lib/tipos";
import { CurvaNotas, FichaDetalle, type Ficha } from "../MiCuenta";
import { Button, Card, Chip, EmptyState, Input, Tabs, TabPill, Textarea, cn, useToast } from "../../components/ui";

// Panel «Aprendizaje» (brief 26-oct-2026, paso 3.4): las notas del examen diario, el plan del día y las
// fichas por aprobar. Patrón del research (docs/research/mi-aprendizaje): tres cifras con su referencia,
// cuadrícula persona × día con la nota ESCRITA (el color solo dice aprobó / no / no rindió), la curva con
// la línea del 10 y lo más fallado; fichas en cola con la fuente al lado. Sin podio entre vendedoras ni
// «aprobar todas» (en salud se mira cada ficha con su fuente).

type Nota = {
  id: string; usuario_id: string; usuario_nombre: string | null; sucursal_nombre: string; fecha: string; puntaje: number; puntaje_max: number; aprobado: boolean;
  falladas: { orden: number; tipo: string; enunciado: string; respuesta: string | null; correcta: string }[];
};
type FichaAdmin = Ficha & { clave: string; estado: "borrador" | "aprobada"; aprobada_at: string | null };
type Tab = "notas" | "plan" | "fichas";

const diasAtras = (n: number) => Array.from({ length: n }, (_, k) => ahoraFecha(-(n - 1 - k)));
const dm = (ymd: string) => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}`;
const TIPO_CORTO: Record<string, string> = { generico: "Marca y genérico", receta: "¿Pide receta?", para_que: "¿Para qué sirve?", caso: "Caso de mostrador", practica: "Práctica" };

export function Aprendizaje({ sesion }: { sesion: SesionActiva }) {
  const esDueno = sesion.usuario.rol === "super_admin";
  const [tab, setTab] = useState<Tab>("notas");
  const borradores = useApi<{ fichas: FichaAdmin[] }>("/aprendizaje/fichas/todas?estado=borrador");
  const nBorr = borradores.data?.fichas.length ?? null;
  // El Dueño elige la botica para el plan (las notas pueden ser de toda la cadena). Dos estados aparte: en
  // Notas el vacío ES «Toda la cadena» y es lo primero que ve; el Plan sí necesita una botica. Con uno solo,
  // el efecto que pone la primera botica pisaba «Toda la cadena» y el Dueño no veía la nota de VES (28-oct).
  const sucursales = useApi<{ sucursales: { id: string; nombre: string }[] }>(esDueno ? "/sucursales" : null);
  const [sucNotas, setSucNotas] = useState("");
  const [sucPlan, setSucPlan] = useState("");
  useEffect(() => {
    const primera = sucursales.data?.sucursales[0]?.id;
    if (esDueno && !sucPlan && primera) setSucPlan(primera);
  }, [esDueno, sucPlan, sucursales.data]);
  const suc = tab === "notas" ? sucNotas : sucPlan;
  const setSuc = tab === "notas" ? setSucNotas : setSucPlan;

  return (
    <div className="flex flex-col gap-[18px]">
      <Tabs className="flex-wrap">
        <TabPill active={tab === "notas"} onClick={() => setTab("notas")} className="max-md:min-h-[44px]">
          Notas del equipo
        </TabPill>
        <TabPill active={tab === "plan"} onClick={() => setTab("plan")} className="max-md:min-h-[44px]">
          Plan del día
        </TabPill>
        <TabPill active={tab === "fichas"} onClick={() => setTab("fichas")} className="max-md:min-h-[44px]">
          Fichas por aprobar{nBorr != null ? ` (${nBorr})` : ""}
        </TabPill>
      </Tabs>
      {esDueno && tab !== "fichas" && sucursales.data && (
        <label className="flex items-center gap-2 text-[12.5px] text-ink-2">
          Botica:
          <select value={suc} onChange={(e) => setSuc(e.target.value)} className="min-h-[36px] rounded-[9px] border border-line-input bg-field px-3 text-[13px] text-ink outline-none">
            {tab === "notas" && <option value="">Toda la cadena</option>}
            {sucursales.data.sucursales.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nombre}
              </option>
            ))}
          </select>
        </label>
      )}
      {tab === "notas" && <Notas sucursalId={esDueno ? sucNotas : ""} />}
      {tab === "plan" && (!esDueno || sucPlan) && <Plan sucursalId={esDueno ? sucPlan : ""} />}
      {tab === "fichas" && <FichasPorAprobar esDueno={esDueno} onCambio={borradores.recargar} />}
    </div>
  );
}

// ---------------- Notas ----------------
function Notas({ sucursalId }: { sucursalId: string }) {
  const { data, cargando } = useApi<{ notas: Nota[] }>(`/aprendizaje/equipo/notas?dias=14${sucursalId ? `&sucursal_id=${sucursalId}` : ""}`, [sucursalId]);
  const notas = useMemo(() => data?.notas ?? [], [data]);
  const semana = useMemo(() => diasAtras(7), []);
  const semanaPasada = diasAtras(14).slice(0, 7);
  const personas = useMemo(() => {
    const m = new Map<string, { nombre: string; botica: string; porDia: Map<string, Nota> }>();
    for (const n of notas) {
      const p = m.get(n.usuario_id) ?? { nombre: n.usuario_nombre ?? "—", botica: n.sucursal_nombre, porDia: new Map() };
      p.porDia.set(n.fecha, n);
      m.set(n.usuario_id, p);
    }
    return [...m.entries()].sort((a, b) => a[1].nombre.localeCompare(b[1].nombre, "es"));
  }, [notas]);
  const prom = (fs: string[]) => {
    const xs = notas.filter((n) => fs.includes(n.fecha));
    return xs.length ? xs.reduce((s, n) => s + n.puntaje, 0) / xs.length : null;
  };
  const promSemana = prom(semana);
  const promAntes = prom(semanaPasada);
  const hoy = semana[semana.length - 1]!;
  const rindieronHoy = personas.filter(([, p]) => p.porDia.has(hoy)).length;
  const masFalladas = useMemo(() => {
    const c = new Map<string, { texto: string; n: number }>();
    for (const n of notas.filter((x) => semana.includes(x.fecha))) {
      for (const f of n.falladas) {
        const k = `${f.tipo}|${f.enunciado}`;
        c.set(k, { texto: f.enunciado, n: (c.get(k)?.n ?? 0) + 1 });
      }
    }
    return [...c.values()].sort((a, b) => b.n - a.n).slice(0, 5);
  }, [notas, semana]);
  const porTipo = useMemo(() => {
    const t = new Map<string, number>();
    for (const n of notas.filter((x) => semana.includes(x.fecha))) for (const f of n.falladas) t.set(f.tipo, (t.get(f.tipo) ?? 0) + 1);
    return [...t.entries()].sort((a, b) => b[1] - a[1]);
  }, [notas, semana]);
  const [abierta, setAbierta] = useState<string | null>(null);
  const notaAbierta = notas.find((n) => n.id === abierta) ?? null;
  // En el celular la cuadrícula no entra entera: arranca pegada a la derecha para que HOY se vea sin
  // deslizar (28-oct, captura a 390: la columna del día quedaba escondida). En escritorio no se mueve.
  const cuadricula = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = cuadricula.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [cargando, notas.length]);

  if (cargando) return <p className="p-6 text-center text-[13px] text-ink-3">Cargando notas…</p>;
  if (notas.length === 0) return <EmptyState title="Todavía nadie dio el examen" subtitle="La nota aparece aquí apenas alguien lo termine. El examen sale con lo que cada persona carga en Inventario › Ingreso." />;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <span className="text-[12px] text-ink-2">Promedio de la semana</span>
          <span className="text-[26px] font-bold tabular-nums text-ink">{promSemana === null ? "—" : `${promSemana.toFixed(1)}/12`}</span>
          <span className="text-[12px] text-ink-2">{promAntes === null ? "sin semana anterior" : `semana pasada: ${promAntes.toFixed(1)}`}</span>
        </Card>
        <Card>
          <span className="text-[12px] text-ink-2">Dieron el examen hoy</span>
          <span className="text-[26px] font-bold tabular-nums text-ink">
            {rindieronHoy} de {personas.length}
          </span>
          <span className="text-[12px] text-ink-2">de quienes ya rindieron alguno</span>
        </Card>
        <Card>
          <span className="text-[12px] text-ink-2">Lo que más fallan</span>
          <span className="text-[17px] font-bold leading-snug text-ink">{porTipo[0] ? TIPO_CORTO[porTipo[0][0]] : "—"}</span>
          <span className="text-[12px] text-ink-2">{porTipo[0] ? `${porTipo[0][1]} ${porTipo[0][1] === 1 ? "vez" : "veces"} esta semana` : "nada fallado esta semana"}</span>
        </Card>
      </div>

      {/* Cuadrícula persona × día: la nota escrita; el color solo dice aprobó / no aprobó / no rindió. */}
      <Card className="overflow-hidden p-0">
        {/* El título va fuera de lo que se desliza: si fuera `caption`, a 390 se iba con la cuadrícula. */}
        <p id="titulo-cuadricula" className="px-4 pb-1 pt-3 text-left text-[11px] font-bold uppercase tracking-[0.09em] text-ink-3">Últimos 7 días · aprueba con {PUNTAJE_APRUEBA}</p>
        <div ref={cuadricula} className="overflow-x-auto">
        <table aria-labelledby="titulo-cuadricula" className="w-full min-w-[640px] border-collapse text-[13px]">
          <thead>
            <tr className="border-b border-line-row-head text-[11.5px] text-ink-3">
              {/* «Persona» queda fija al deslizar: la cuadrícula arranca a la derecha y el nombre no se pierde. */}
              <th className="sticky left-0 z-[1] bg-card px-4 py-2 text-left font-semibold">Persona</th>
              {semana.map((d) => (
                <th key={d} className="px-1 py-2 text-center font-semibold">
                  {dm(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {personas.map(([id, p]) => (
              <tr key={id} className="border-b border-line-row">
                <td className="sticky left-0 z-[1] bg-card px-4 py-2">
                  <span className="block font-semibold text-ink">{p.nombre}</span>
                  <span className="block text-[11.5px] text-ink-2">{p.botica}</span>
                </td>
                {semana.map((d) => {
                  const n = p.porDia.get(d);
                  return (
                    <td key={d} className="px-1 py-1.5 text-center">
                      {n ? (
                        <button
                          type="button"
                          onClick={() => setAbierta(abierta === n.id ? null : n.id)}
                          aria-label={`${p.nombre}, ${dm(d)}: ${n.puntaje} de ${n.puntaje_max}, ${n.aprobado ? "aprobó" : "no aprobó"}`}
                          className={cn("min-h-[36px] min-w-[48px] rounded-[8px] px-2 font-bold tabular-nums", n.aprobado ? "bg-ok-soft text-ok" : "bg-warn-soft text-warn", abierta === n.id && "ring-2 ring-ink-surface-2")}
                        >
                          {n.puntaje}
                        </button>
                      ) : (
                        <span className="text-ink-3" aria-label="no rindió">
                          —
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>

      {notaAbierta && (
        <Card>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-[15px] font-bold text-ink">
              {notaAbierta.usuario_nombre} · {dm(notaAbierta.fecha)} · {notaAbierta.puntaje}/{notaAbierta.puntaje_max}
            </h3>
            <button type="button" onClick={() => setAbierta(null)} className="text-[12.5px] text-link underline">
              Cerrar
            </button>
          </div>
          {notaAbierta.falladas.length === 0 ? (
            <p className="mt-2 text-[13px] text-ok">No falló ninguna.</p>
          ) : (
            <ul className="mt-2 flex flex-col gap-2">
              {notaAbierta.falladas.map((f) => (
                <li key={f.orden} className="rounded-[10px] border border-line-input p-2.5 text-[13px]">
                  <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">{TIPO_CORTO[f.tipo] ?? f.tipo}</span>
                  <p className="font-semibold text-ink">{f.enunciado}</p>
                  <p className="text-accent-ink">Respondió: {f.respuesta ?? "sin responder"}</p>
                  <p className="text-ok">Correcta: {f.correcta}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        {personas.map(([id, p]) => (
          <Card key={id}>
            <span className="text-[13px] font-semibold text-ink">Curva de {p.nombre}</span>
            <CurvaNotas puntos={semana.map((d) => ({ etiqueta: dm(d), valor: p.porDia.get(d)?.puntaje ?? null, maximo: p.porDia.get(d)?.puntaje_max ?? 12 }))} />
          </Card>
        ))}
        <Card>
          <span className="text-[13px] font-semibold text-ink">Preguntas más falladas de la semana</span>
          {masFalladas.length === 0 ? (
            <p className="mt-2 text-[13px] text-ink-2">Ninguna.</p>
          ) : (
            <ol className="mt-2 flex flex-col gap-1.5 text-[13px] text-ink">
              {masFalladas.map((m) => (
                <li key={m.texto} className="flex gap-2">
                  <span className="flex-none font-bold tabular-nums text-warn">{m.n}×</span>
                  <span>{m.texto}</span>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
    </div>
  );
}

// ---------------- Plan del día ----------------
type Tarea = { id: string; usuario_id: string; usuario_nombre: string | null; texto: string; hecha_at: string | null };
function Plan({ sucursalId }: { sucursalId: string }) {
  const toast = useToast();
  const [fecha, setFecha] = useState(ahoraFecha());
  const q = (p: string) => `${p}${p.includes("?") ? "&" : "?"}${sucursalId ? `sucursal_id=${sucursalId}` : ""}`;
  const personas = useApi<{ personas: { id: string; nombre: string; rol: string }[] }>(q("/aprendizaje/equipo/personas"), [sucursalId]);
  const tareas = useApi<{ tareas: Tarea[] }>(q(`/aprendizaje/equipo/tareas?fecha=${fecha}`), [sucursalId, fecha]);
  const [para, setPara] = useState("");
  const [texto, setTexto] = useState("");
  useEffect(() => {
    const primera = personas.data?.personas[0]?.id;
    if (!para && primera) setPara(primera);
  }, [para, personas.data]);

  async function agregar() {
    try {
      await mutar(q("/aprendizaje/equipo/tareas"), { method: "POST", body: { usuario_id: para, fecha, texto: texto.trim() } });
      setTexto("");
      tareas.recargar();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }
  async function borrar(id: string) {
    try {
      await mutar(q(`/aprendizaje/equipo/tareas/${id}`), { method: "DELETE" });
      tareas.recargar();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }
  const lista = tareas.data?.tareas ?? [];
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
      <Card>
        <h3 className="text-[15px] font-bold text-ink">Programar una tarea</h3>
        <form
          className="mt-3 flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (texto.trim() && para) void agregar();
          }}
        >
          <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-ink-2">
            Día
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="min-h-[44px]" />
          </label>
          <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-ink-2">
            Para
            <select value={para} onChange={(e) => setPara(e.target.value)} className="min-h-[44px] rounded-[9px] border border-line-input bg-field px-3 text-[13px] text-ink outline-none">
              {(personas.data?.personas ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                  {p.rol === "admin_sucursal" ? " (Encargado)" : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-ink-2">
            Tarea
            <Textarea rows={3} maxLength={300} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Ej. Cargar la repisa de antibióticos y estudiar sus fichas" />
          </label>
          <Button type="submit" disabled={!texto.trim() || !para} className="min-h-[44px] justify-center">
            Agregar al plan
          </Button>
        </form>
      </Card>
      <Card>
        <h3 className="text-[15px] font-bold text-ink">Plan del {dm(fecha)}</h3>
        {lista.length === 0 ? (
          <p className="mt-2 text-[13px] text-ink-2">Nada programado para este día.</p>
        ) : (
          <ul className="mt-2 flex flex-col">
            {lista.map((t) => (
              <li key={t.id} className="flex items-center gap-3 border-t border-line-row py-2.5">
                <Chip variant={t.hecha_at ? "ok" : "neutral"}>{t.hecha_at ? "Hecha" : "Pendiente"}</Chip>
                <span className="min-w-0 flex-1 text-[13.5px] text-ink">
                  <span className="font-semibold">{t.usuario_nombre}</span> · {t.texto}
                </span>
                <button type="button" onClick={() => void borrar(t.id)} aria-label={`Quitar tarea de ${t.usuario_nombre ?? ""}`} className="h-9 w-9 rounded-[8px] text-ink-3 hover:bg-hover-btn hover:text-accent-ink">
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ---------------- Fichas por aprobar ----------------
function FichasPorAprobar({ esDueno, onCambio }: { esDueno: boolean; onCambio: () => void }) {
  const toast = useToast();
  const [filtro, setFiltro] = useState<"borrador" | "aprobada">("borrador");
  const { data, recargar } = useApi<{ fichas: FichaAdmin[] }>(`/aprendizaje/fichas/todas?estado=${filtro}`, [filtro]);
  const fichas = data?.fichas ?? [];
  const [sel, setSel] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const actual = fichas.find((f) => f.id === sel) ?? fichas[0] ?? null;
  useEffect(() => setEditando(false), [actual?.id]);

  async function cambiarEstado(f: FichaAdmin, accion: "aprobar" | "devolver") {
    try {
      await mutar(`/aprendizaje/fichas/${f.id}/${accion}`, { method: "POST" });
      const idx = fichas.findIndex((x) => x.id === f.id);
      setSel(fichas[idx + 1]?.id ?? fichas[idx - 1]?.id ?? null);
      recargar();
      onCambio();
      toast(accion === "aprobar" ? `${f.nombre_mostrar}: aprobada. Ya la ve la vendedora.` : `${f.nombre_mostrar}: volvió a borrador.`);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <Tabs>
        <TabPill active={filtro === "borrador"} onClick={() => setFiltro("borrador")}>
          En borrador
        </TabPill>
        <TabPill active={filtro === "aprobada"} onClick={() => setFiltro("aprobada")}>
          Aprobadas
        </TabPill>
      </Tabs>
      {!esDueno && <p className="text-[12.5px] text-ink-2">Las fichas las aprueba el Dueño. Aquí puedes leerlas.</p>}
      {fichas.length === 0 ? (
        <EmptyState title={filtro === "borrador" ? "No hay fichas por aprobar" : "Todavía no hay fichas aprobadas"} />
      ) : (
        <div className="grid gap-3 lg:grid-cols-[300px_minmax(0,1fr)]">
          <ul className="flex max-h-[70vh] flex-col gap-1 overflow-y-auto rounded-[12px] border border-line bg-card p-1.5" aria-label="Fichas">
            {fichas.map((f) => (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => setSel(f.id)}
                  aria-current={actual?.id === f.id ? "true" : undefined}
                  className={cn("flex min-h-[48px] w-full flex-col rounded-[9px] px-3 py-2 text-left", actual?.id === f.id ? "bg-sel" : "hover:bg-hover-btn")}
                >
                  <span className="text-[13.5px] font-semibold text-ink">{f.nombre_mostrar}</span>
                  <span className="truncate text-[12px] text-ink-2">{f.para_que_sirve}</span>
                </button>
              </li>
            ))}
          </ul>
          {actual && (
            <Card className="gap-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="text-[19px] font-bold text-ink">{actual.nombre_mostrar}</h3>
                  <p className="text-[13.5px] text-ink-2">{actual.para_que_sirve}</p>
                </div>
                <div className="flex gap-1.5">
                  <Chip variant={actual.suele_pedir_receta ? "danger" : "ok"}>{actual.suele_pedir_receta ? "Suele pedir receta" : "Venta libre"}</Chip>
                  <Chip variant={actual.estado === "aprobada" ? "ok" : "warn"}>{actual.estado === "aprobada" ? "Aprobada" : "Borrador"}</Chip>
                </div>
              </div>
              <div className="rounded-[10px] bg-inset px-3 py-2 text-[12.5px] text-ink">
                <span className="font-bold">Fuente: </span>
                {actual.fuente}
              </div>
              {editando ? (
                <EditarFicha f={actual} onListo={() => { setEditando(false); recargar(); onCambio(); }} />
              ) : (
                <div className="rounded-[10px] border border-line-input">
                  <FichaDetalle f={actual} />
                </div>
              )}
              {esDueno && !editando && (
                <div className="sticky bottom-0 flex flex-wrap gap-2 bg-card pt-1">
                  {actual.estado === "borrador" ? (
                    <Button onClick={() => void cambiarEstado(actual, "aprobar")} className="min-h-[48px] bg-ok hover:bg-ok hover:opacity-90">
                      Aprobar
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={() => void cambiarEstado(actual, "devolver")} className="min-h-[48px]">
                      Devolver a borrador
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setEditando(true)} className="min-h-[48px]">
                    Corregir
                  </Button>
                </div>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

const CAMPOS: [keyof Ficha, string, number][] = [
  ["para_que_sirve", "Para qué sirve (una línea, es la respuesta del examen)", 90],
  ["indicaciones", "Para qué sirve (detalle)", 2000],
  ["posologia", "Cómo se toma", 2000],
  ["efectos_secundarios", "Efectos que puede dar", 2000],
  ["contraindicaciones", "Quién no debe tomarlo", 2000],
  ["cuidados_al_entregar", "Al entregar", 2000],
  ["cuando_llamar_al_quimico", "Llama al químico si…", 2000],
  ["fuente", "Fuente", 2000],
];

function EditarFicha({ f, onListo }: { f: FichaAdmin; onListo: () => void }) {
  const toast = useToast();
  const [v, setV] = useState<Record<string, string>>(() => Object.fromEntries(CAMPOS.map(([k]) => [k, String(f[k] ?? "")])));
  const [receta, setReceta] = useState(f.suele_pedir_receta === 1);
  async function guardar() {
    try {
      await mutar(`/aprendizaje/fichas/${f.id}`, { method: "PUT", body: { ...v, suele_pedir_receta: receta } });
      toast("Ficha corregida. Queda en borrador hasta que la apruebes.");
      onListo();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }
  return (
    <div className="flex flex-col gap-3">
      {CAMPOS.map(([k, etiqueta, max]) => (
        <label key={k} className="flex flex-col gap-1 text-[12.5px] font-semibold text-ink-2">
          {etiqueta}
          {max <= 90 ? <Input value={v[k]} maxLength={max} onChange={(e) => setV({ ...v, [k]: e.target.value })} /> : <Textarea rows={3} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} />}
        </label>
      ))}
      <label className="flex items-center gap-2 text-[13px] text-ink">
        <input type="checkbox" className="h-5 w-5 accent-accent" checked={receta} onChange={(e) => setReceta(e.target.checked)} />
        Suele pedir receta
      </label>
      <div className="flex gap-2">
        <Button onClick={() => void guardar()} className="min-h-[44px]">
          Guardar
        </Button>
        <Button variant="outline" onClick={onListo} className="min-h-[44px]">
          Cancelar
        </Button>
      </div>
    </div>
  );
}
